import {
  Document,
  Font,
  Page,
  Path,
  pdf,
  StyleSheet,
  Svg,
  Text,
  View
} from "@react-pdf/renderer"
import { convertNumber, useHandleAuth } from "utils"
import moment from "moment"
import { buildHierarchys } from "./build-hierarchy"
import { BalanceSheetReportType } from "../types"

type UseExportPdf = {
  data: BalanceSheetReportType[]
}

type HandleExportPdfType = {
  date?: string
  endDate?: string
  displayAccountNumber?: boolean
}

export const useExportPdf = ({ data }: UseExportPdf) => {
  const mapData: any[] = []
  const auth = useHandleAuth()
  const token = auth.userTokenData()

  const parents = data.filter((item) => item.parent_id === -1)

  const mapDataHirarki = parents?.map((item) => {
    const hierarchy = buildHierarchys(data, item.coa_id)
    return {
      ...item,
      children: hierarchy
    }
  })

  makeRowsPdf(mapDataHirarki, mapData)

  return {
    handleExportPdf: ({
      date,
      endDate,
      displayAccountNumber
    }: HandleExportPdfType) =>
      pdf(
        <TableProfitLoss
          companyName={token?.company_name ?? ""}
          data={mapData}
          date={date}
          endDate={endDate}
          displayAccountNumber={displayAccountNumber}
        />
      )
        .toBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement("a")
          link.href = url
          link.download = "buget-variance.pdf"
          link.click()
          URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.error("Gagal membuat PDF:", error)
        })
  }
}

export const makeRowsPdf = (data: any, mapData: any[]) => {
  data.forEach((item: any) => {
    let itemRow = {
      ...item
    }

    if (
      ["NET ASSETS", "NET PROFIT"].includes(item.coa_name) ||
      item.header_level !== 3
    ) {
      itemRow.bold = true
    }

    mapData.push(itemRow)

    if (item.children?.length > 0) {
      makeRowsPdf(item.children, mapData)
    }

    if (
      item.header_level !== 3 &&
      !["NET ASSETS", "NET PROFIT"].includes(item.coa_name)
    ) {
      mapData.push({
        ...item,
        coa_name: `Total ${item.coa_name}`,
        bold: true
      })
    }
  })
}

type TableProfitLossProps = {
  companyName: string
  data: any[]
  endDate?: string
  date?: string
  displayAccountNumber?: boolean
}

export const TableProfitLoss = ({
  companyName,
  data,
  endDate,
  date,
  displayAccountNumber,
}: TableProfitLossProps) => {
  Font.register({
    family: "Roboto",
    fonts: [
      { src: "https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxP.ttf" },
      {
        src: "https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBBc9.ttf",
        fontWeight: "bold",
      },
    ],
  })

  const styles = StyleSheet.create({
    table: {
      display: "flex",
      width: "auto",
    },
    tableRow: {
      flexDirection: "row",
    },
    tableCol: {
      flex: 1,
    },
    tableCell: {
      margin: 2,
      fontSize: 8,
      fontFamily: "Roboto",
    },
    headerText: {
      margin: 2,
      fontSize: 9,
      fontWeight: "bold",
      fontFamily: "Roboto",
    },
  })

  return (
    <Document>
      <Page
        size="A4"
        style={{
          paddingHorizontal: 15,
          paddingBottom: 35,
          paddingTop: 15,
          position: "relative",
        }}
      >
        <Text
          style={{
            fontSize: 22,
            marginTop: 30,
            marginBottom: 8,
            textAlign: "left",
            fontWeight: "heavy",
          }}
        >
          BUGET VARIANCE
        </Text>

        <Text
          style={{
            fontSize: 14,
            marginBottom: 4,
            textAlign: "left",
            fontWeight: "bold",
          }}
        >
          {companyName?.toUpperCase() ?? ""}
        </Text>

        <Text
          style={{
            fontSize: 14,
            marginBottom: 4,
            textAlign: "left",
            fontWeight: "bold",
          }}
        >
          From {moment(date).format("MMMM YYYY")} to{" "}
          {moment(endDate).format("MMMM YYYY")}
        </Text>

        <View style={styles.table}>
          {/* Header */}
          <View
            style={{
              ...styles.tableRow,
              borderBottomColor: "#000",
              borderBottomStyle: "solid",
              borderBottomWidth: 0.5,
            }}
          >
            <View style={[styles.tableCol, { flex: 2 }]}>
              <Text style={[styles.headerText]}>Account</Text>
            </View>
            <View style={[styles.tableCol]}>
              <Text
                style={[styles.headerText, { textAlign: "right", fontSize: 7 }]}
              >
               {`${moment(date).format("MM/YYYY")} - ${moment(endDate).format("MM/YYYY")}`}
              </Text>
            </View>
            <View style={[styles.tableCol]}>
              <Text
                style={[styles.headerText, { textAlign: "right", fontSize: 7 }]}
              >
                {`${moment(date).format("MM/YYYY")} - ${moment(endDate).format("MM/YYYY")}`}
              </Text>
            </View>
            <View style={[styles.tableCol]}>
              <Text
                style={[styles.headerText, { textAlign: "right", fontSize: 7 }]}
              >
                Variance
              </Text>
            </View>
            <View style={[styles.tableCol]}>
              <Text
                style={[styles.headerText, { textAlign: "right", fontSize: 7 }]}
              >
                Variance %
              </Text>
            </View>
          </View>

          {/* Data Rows */}
          {data.map((item: any, rowIndex: number) => {
            const actual = item.actual ?? 0
            const budget = item.budget ?? 0
            const variance = item.variance ?? 0
            const variancePct = Number(item.percent_variance) ?? 0

            return (
              <View
                wrap={false}
                key={rowIndex}
                style={[styles.tableRow, { borderBottomWidth: 0.5, borderBottomColor: "#ccc" }]}
              >
                <View style={[styles.tableCol, { flex: 2 }]}>
                  <Text
                    style={[
                      styles.tableCell,
                      { fontWeight: item.bold ? "bold" : "normal" },
                    ]}
                  >
                    {displayAccountNumber ? item.coacode + " " : ""}
                    {item.coa_name}
                  </Text>
                </View>

                {/* Actual */}
                <View style={[styles.tableCol]}>
                  <Text style={[styles.tableCell, { textAlign: "right" }]}>
                    {actual
                      ? convertNumber(actual).intoCurrency
                      : "-"}
                  </Text>
                </View>

                {/* Budget */}
                <View style={[styles.tableCol]}>
                  <Text style={[styles.tableCell, { textAlign: "right" }]}>
                    {budget
                      ? convertNumber(budget).intoCurrency
                      : "-"}
                  </Text>
                </View>

                {/* Variance */}
                <View style={[styles.tableCol]}>
                  <View style={{ flexDirection: "row", justifyContent: "flex-end", alignItems: "center" }}>
                    <Text
                      style={[
                        styles.tableCell,
                        { textAlign: "right", marginRight: 3 }
                      ]}
                    >
                      {variance
                        ? convertNumber(Math.abs(variance)).intoCurrency
                        : "-"}
                    </Text>

                    {variance > 0 ? (
                      <Svg width="6" height="6" viewBox="0 0 24 24">
                        <Path d="M12 4l-8 16h16z" fill="green" />
                      </Svg>
                    ) : variance < 0 ? (
                      <Svg width="6" height="6" viewBox="0 0 24 24">
                        <Path d="M12 20l8-16H4z" fill="red" />
                      </Svg>
                    ) : (
                      <Text></Text>
                    )}
                  </View>
                </View>

                {/* Variance  % */}
                <View style={[styles.tableCol]}>
                  <Text
                    style={[
                      styles.tableCell,
                      { textAlign: "right" },
                    ]}
                  >
                    {variancePct
                      ? variancePct.toFixed(2) + "%"
                      : "-"}
                  </Text>
                </View>
              </View>
            )
          })}
        </View>
      </Page>
    </Document>
  )
}
